import useUploadEvents, { ResourceInfo } from '@/hooks/useUploadEvents';
import useUploadSlots from '@/hooks/useUploadSlots';
import { setFailed } from '@/pages/cvc/util/items';
import { CvCResultFileInfo, resultFileListModel } from '@/stores/cvc';
import { resultUploadingStatesModel } from '@/stores/resource';
import { uploadAllAndTrigger } from '@/util/upload';
import { useCallback } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';

const useResultUpload = () => {
  const [uploadingStatus, setUploadingStatus] = useRecoilState(
    resultUploadingStatesModel
  );
  const setResultList = useSetRecoilState(resultFileListModel);
  const completeUpload = useCallback(
    (data: ResourceInfo, resource_id: string) => {
      setResultList((prev) => {
        return prev.map((item) => {
          if (item.id === resource_id) {
            return { ...item, ...data };
          }
          return item;
        });
      });
    },
    [setResultList]
  );
  const requestSlots = useUploadSlots();
  // Resolves with the created resource ids — the ids that own the new
  // result-list entries — so callers (AudioEditorPanel save-as-new) can
  // record the id whose WebSocket completion events they must follow
  // (ISSUE-028).
  const addFiles = useCallback(
    async (sessionId: string, files: File[]): Promise<string[]> => {
      // See SourcePanel.addFiles. The empty return matters here beyond the
      // other call sites: AudioEditorPanel's save-as-new destructures
      // `[createdResourceId]` off this array, so a refused slot resolves to
      // `undefined` and its `shouldUpdateEditFileId && createdResourceId`
      // guard already declines to re-point the editor at a resource that was
      // never created (ISSUE-028).
      const slots = await requestSlots(sessionId, files);
      if (!slots.length) return [];
      const resources = slots.map(
        ({ file, data }) =>
          ({
            name: file.name,
            id: data.resource_id,
            resource_id: data.resource_id,
            isChecked: true,
            upload_url: data.upload_url,
          } as CvCResultFileInfo)
      );
      setUploadingStatus((prev) => {
        const status = { ...prev };
        resources.forEach((file) => {
          status[file.id] = 'READY';
        });
        return status;
      });
      setResultList(
        (list = []) => resources.concat(list) as CvCResultFileInfo[]
      );
      await uploadAllAndTrigger(
        slots.map(({ file, data }) => ({
          resourceId: data.resource_id,
          uploadUrl: data.upload_url,
          file,
        })),
        (resourceId) =>
          setUploadingStatus((prev) => setFailed(prev, resourceId))
      );
      return resources.map((item) => item.id);
    },
    [requestSlots, setResultList, setUploadingStatus]
  );

  return useUploadEvents(
    uploadingStatus,
    setUploadingStatus,
    completeUpload,
    addFiles
  );
};
export default useResultUpload;
